import { FC, useCallback, useReducer } from 'react'
import { Tooltip, ITooltip } from 'react-tooltip'

export type ReactTooltipProps = ITooltip

/**
 * ```
 * import { ReactTooltip } from '@/components/Tooltip/ReactTooltip'
 * ```
 *
 * A tooltip component that uses the react-tooltip library.
 *
 * ```
 * <div
 *  data-tooltip-id='tooltip'
 *  data-tooltip-content='This is a tooltip'
 * >
 *  <Button />
 * </div>
 * <ReactTooltip id='tooltip' place='top' />
 * ```
 */

/*
  The design system's Tooltip has a 1px `border/default` stroke (MAM-1000).
  It goes through react-tooltip's `border` prop rather than a class because
  the library also draws it on the two outer sides of the arrow and moves the
  arrow out by the border width, so the outline runs unbroken around the
  arrow. A class border stops at the arrow. Every tooltip gets it, including
  callers that replace the default className. Tailwind emits the theme
  variable only while scanned source names it, and this string does.
*/
const DESIGN_SYSTEM_BORDER = '1px solid var(--color-border-inverse)'

/*
  A close that lands before the tooltip has shown remounts it (MAM-1080).
  react-tooltip (5.28, and still 6.0.8) mounts the node with the closing class
  and sets its `show` state 10 ms later, and it schedules the unmount only when
  its `show` effect sees `true` turn `false`. Two closes skip that:
  - Pointer or focus leaving inside those 10 ms on a busy main thread: the show
    and hide timers fire back to back and render together, so `show` never
    changes. The library calls `setIsOpen` in the same timer as each `setShow`.
  - A controlled `isOpen` turning `false` inside those 10 ms: the library's
    `[isOpen]` effect cancels the pending show timer, so `show` never changes,
    and it doesn't call `setIsOpen`.
  Either way the transparent node stays in the accessibility tree as a
  `tooltip`. The library reports `afterShow` from its `show` effect and
  `afterHide` when it unmounts. A close (`setIsOpen(false)` or `isOpen` turning
  `false`) after an open that hasn't reached `afterShow` marks the open
  abandoned, and the wrapper decides at its next render: React runs pending
  effects before rendering, so an `afterShow` from a show that did commit has
  been reported by then even when the close was queued first (a loaded thread
  can run the hide timer between that commit and its effects), and a reopen in
  the same batch cancels it. Still abandoned, the tooltip gets a new `key`: the
  library starts again unmounted, without a fade for a tooltip that never
  showed, and the next hover, focus or `isOpen` opens it as usual.
  `defaultIsOpen` applies to the first instance only, so a remount doesn't
  reopen what the user just left. Callers' own `setIsOpen`/`afterShow`/
  `afterHide` still run. The callbacks keep their identity across renders:
  react-tooltip 6.x clears its pending show, hide and fade timers whenever
  `setIsOpen` changes.
*/
type CloseGuard = {
  phase: 'hidden' | 'opening' | 'abandoned' | 'shown'
  // The tooltip's `key`; a replaced instance's late reports are ignored.
  instance: number
  // The controlled `isOpen` last seen, to notice the caller changing it.
  isOpen: boolean | undefined
}

type CloseGuardEvent =
  | { type: 'open' | 'close' | 'remount' }
  | { type: 'controlled'; isOpen: boolean | undefined }
  | { type: 'shown' | 'hidden'; instance: number }

const openGuard = (guard: CloseGuard): CloseGuard =>
  guard.phase === 'hidden' || guard.phase === 'abandoned'
    ? { ...guard, phase: 'opening' }
    : guard

const closeGuard = (guard: CloseGuard): CloseGuard =>
  guard.phase === 'opening' ? { ...guard, phase: 'abandoned' } : guard

const reduceCloseGuard = (
  guard: CloseGuard,
  event: CloseGuardEvent
): CloseGuard => {
  switch (event.type) {
    case 'open':
      return openGuard(guard)
    case 'close':
      return closeGuard(guard)
    case 'controlled': {
      const next = { ...guard, isOpen: event.isOpen }
      return event.isOpen ? openGuard(next) : closeGuard(next)
    }
    case 'remount':
      return guard.phase === 'abandoned'
        ? { ...guard, phase: 'hidden', instance: guard.instance + 1 }
        : guard
    case 'shown':
    case 'hidden':
      return event.instance === guard.instance
        ? { ...guard, phase: event.type }
        : guard
  }
}

const ReactTooltip: FC<ReactTooltipProps> = ({
  id = '',
  className = 'z-9999 text-xs! text-white! px-4! py-2! bg-primary!',
  place = 'top',
  offset = 12,
  delayShow = 50,
  delayHide = 0,
  border = DESIGN_SYSTEM_BORDER,
  isOpen,
  defaultIsOpen,
  setIsOpen,
  afterShow,
  afterHide,
  ...rest
}) => {
  const [guard, dispatch] = useReducer(
    reduceCloseGuard,
    isOpen,
    (initialIsOpen): CloseGuard => ({
      phase: initialIsOpen ? 'opening' : 'hidden',
      instance: 0,
      isOpen: initialIsOpen,
    })
  )
  // Both adjust state during render, not in an effect.
  if (isOpen !== guard.isOpen) dispatch({ type: 'controlled', isOpen })
  if (guard.phase === 'abandoned') dispatch({ type: 'remount' })

  const { instance } = guard

  const handleSetIsOpen = useCallback(
    (value: boolean) => {
      setIsOpen?.(value)
      dispatch({ type: value ? 'open' : 'close' })
    },
    [setIsOpen]
  )

  const handleAfterShow = useCallback(() => {
    dispatch({ type: 'shown', instance })
    afterShow?.()
  }, [instance, afterShow])

  const handleAfterHide = useCallback(() => {
    dispatch({ type: 'hidden', instance })
    afterHide?.()
  }, [instance, afterHide])

  return (
    <Tooltip
      key={instance}
      id={id}
      className={className}
      place={place}
      offset={offset}
      delayShow={delayShow}
      delayHide={delayHide}
      border={border}
      isOpen={isOpen}
      defaultIsOpen={instance === 0 ? defaultIsOpen : false}
      setIsOpen={handleSetIsOpen}
      afterShow={handleAfterShow}
      afterHide={handleAfterHide}
      {...rest}
    />
  )
}

export default ReactTooltip
